// src/components/BoardTheme/ThemeGrid.js

import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarExport,
    GridToolbarQuickFilter
} from '@mui/x-data-grid';

import { convertDateToString } from '../../common/ConvertTools.js';
import { getBootstrapColors } from '../race/RaceColors.js';

import gridSxStyle from "../../common/GridSxStyles.js";

const ThemeGrid = React.memo(({ currentUserEmail, currentThemeOwnerEmail, contentTheme, themeId, setThemeId, isDesktop, loadData, removeData, saveData, cloneData, refreshDatas, createNew, shareTheme, isAdmin, editAllowed }) => {

    console.log("ThemeGrid / refresh, editAllowed: " + editAllowed);

    const DEFAULT_PAGINATION_MODEL = { pageSize: 25 };
    const DEFAULT_FILTER_MODEL = { filterModel: { items: [] } };
    const [devicesPaginationModel, setDevicesPaginationModel] = useState(DEFAULT_PAGINATION_MODEL);
    const [devicesFilterModel, setDevicesFilterModel] = useState(DEFAULT_FILTER_MODEL);

    const TableToolbar = (props) => {
        return (
            <GridToolbarContainer>

                <button className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
                    onClick={() => {
                        createNew();
                    }}>
                    <i className="fa-solid fa-circle-plus" style={{ color: 'var(--color-foreground)', marginRight: '8px' }}></i>
                    Create
                </button>
                <button className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
                    onClick={() => {
                        if (editAllowed)
                            saveData();
                        else toast.error("You are not authorized to save this theme");
                    }}>
                    <i className="fa-solid fa-save" style={{ color: (editAllowed) ? 'var(--color-foreground)' : isAdmin ? '#f00' : 'var(--color-grey)', marginRight: '8px' }}></i>
                    Save
                </button>
                <button className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeSmall MuiButton-textSizeSmall MuiCustomButton"
                    onClick={() => {
                        refreshDatas();
                    }}>
                    <i className="fa-solid fa-arrows-rotate" style={{ color: 'var(--color-foreground)', marginRight: '8px' }}></i>
                    Refresh
                </button>
                <GridToolbarFilterButton />
                <GridToolbarQuickFilter />
            </GridToolbarContainer>
        );
    };

    // Define columns using useMemo for performance optimization
    const columns = useMemo(() => [
        {
            field: 'actions',
            headerName: '',
            width: 115,
            resizable: false,
            sortable: false, // Typically, action columns are not sortable
            renderCell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>
                    <i
                        className="fa fa-pen-to-square"
                        style={{
                            color: row.id == themeId ? getBootstrapColors()[0] : "",
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            setThemeId(row.id);
                            loadData(row.id, row.owner_email, row.data);
                        }}
                        title="Edit Theme"
                        aria-label="Edit Theme"
                    />
                    <i
                        className="fa fa-trash"
                        style={{
                            marginLeft: "15px",
                            color: (row.owner_email == currentUserEmail) ? 'var(--color-foreground)' : isAdmin ? '#f00' : 'var(--color-grey)',
                            cursor: (row.owner_email == currentUserEmail) ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                            if ((currentUserEmail == row.owner_email) || isAdmin)
                                removeData(row.id);
                        }}
                        title="Delete Theme"
                        aria-label="Delete Theme"
                    ></i>
                    <i
                        className="fa fa-copy"
                        style={{
                            marginLeft: "15px",
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            cloneData(row.id, row.data);
                        }}
                        title="Clone Theme"
                        aria-label="Clone Theme"
                    ></i>
                    <i
                        className="fa fa-share"
                        style={{
                            marginLeft: "15px",
                            color: (row.shared) ? 'var(--color-foreground)' : 'var(--color-grey)',
                            cursor: ((row.owner_email == currentUserEmail)) ? 'pointer' : 'not-allowed',
                        }}
                        onClick={() => {
                            if ((currentUserEmail == row.owner_email) || isAdmin)
                                shareTheme(row.id, !row.shared);
                        }}
                        title="Share Theme"
                        aria-label="Share Theme"
                    ></i>
                </div>
            )
        },
        {
            field: 'name',
            headerName: 'Name',
            width: 150,
            sortable: true,
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 90,
            sortable: true,
            renderCell: ({ row }) => {
                switch (row.type) {
                    case 0:
                        return <div>Ride</div>;
                    case 1:
                        return <div>Navigation</div>;
                    case 2:
                        return <div>Bootstrap</div>;
                    case 3:
                        return <div>Throttle Calibrations</div>;
                    case 4:
                        return <div>Tech1</div>;
                    case 5:
                        return <div>Tech2</div>;
                    case 6:
                        return <div>Tech3</div>;
                    default:
                        return <div>Unknown</div>;
                }
            }
        },
        {
            field: 'date_edit',
            headerName: 'Date Modified',
            width: 130,
            sortable: true,
            renderCell: ({ row }) => <div>{convertDateToString(row.date_edit)}</div>,
        },
        {
            field: 'id',
            headerName: 'Id',
            width: 15,
        },
        {
            field: 'owner_email',
            headerName: 'Owner',
            width: 110,
            sortable: true,
        },
    ], [themeId, currentUserEmail, loadData, removeData, saveData, cloneData]);

    // No need to conditionally remove the 'id' column
    const displayedColumns = columns;

    const handleFilterModelChange = (filterModel) => {
        setDevicesFilterModel(filterModel);
    };

    const handlePaginationModelChange = (paginationModel) => {
        setDevicesPaginationModel({
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
        });
    }

    return (
        <DataGrid
            disableVirtualization
            disableRowSelectionOnClick
            disableDensitySelector
            columnHeaderHeight={35}
            rowHeight={30}
            rows={contentTheme}
            columns={displayedColumns}
            initialState={{
                pagination: { paginationModel: devicesPaginationModel },
                filter: { filterModel: devicesFilterModel },
                sorting: { sortModel: [{ field: 'id', sort: 'asc' }] }, // Default sorting by 'id' ascending
            }}
            filterModel={devicesFilterModel.filterModel}
            onPaginationModelChange={handlePaginationModelChange}
            onFilterModelChange={handleFilterModelChange}
            slots={{ toolbar: TableToolbar }}

            columnVisibilityModel={{
                id: isAdmin,
                owner_email: isAdmin
            }}

            slotProps={{
                toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                    showExport: false,
                    showFilter: false,
                },
            }}
            pageSizeOptions={[25, 50, 75]}
            sx={gridSxStyle}
        />
    );
} /*, areEqual*/);

export default ThemeGrid;
